import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { LoginModal } from './LoginModal';
import { Shield } from 'lucide-react'; // Assuming Shield is imported from lucide-react
import { Button } from '@/components/ui/button'; // Assuming Button is imported from ui/button

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'admin' | 'therapist' | 'client';
  roles?: string[];
}

export const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  try {
    const { isAuthenticated, isLoading, user } = useAuth();
    const [location, navigate] = useLocation();
    const [isLoginOpen, setIsLoginOpen] = useState(false);

    useEffect(() => {
      if (!isLoading) {
        if (!isAuthenticated) {
          // Save the current path to redirect after login
          console.log('Saving redirect path:', location);
          sessionStorage.setItem('redirect_after_login', location);

          // Redirect to appropriate login page based on required role
          if (requiredRole === 'therapist') {
            navigate('/therapist-login');
          } else if (requiredRole === 'admin') {
            navigate('/admin-login');
          } else {
            setIsLoginOpen(true);
          }
        } else if (requiredRole && user?.role !== requiredRole) {
          // User is authenticated but doesn't have the required role
          if (user?.role === 'admin') {
            navigate('/admin');
          } else if (user?.role === 'therapist') {
            navigate('/therapist-dashboard');
          } else {
            navigate('/my-account');
          }
        }
      }
    }, [isAuthenticated, isLoading, user, requiredRole, location, navigate]);

    const handleLoginSuccess = () => {
      setIsLoginOpen(false);

      // Check for saved redirect path and navigate to it
      const redirectPath = sessionStorage.getItem('redirect_after_login');
      if (redirectPath) {
        sessionStorage.removeItem('redirect_after_login');
        console.log('Redirecting after login to:', redirectPath);
        navigate(redirectPath);
      }
    };

    const handleModalClose = (open: boolean) => {
      setIsLoginOpen(open);
      if (!open && !isAuthenticated) {
        // If user closes modal without logging in, redirect to home
        navigate('/');
      }
    };

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (!isAuthenticated) {
      // For therapist and admin routes, redirect will happen in useEffect
      // Only show login modal for client routes
      if (requiredRole === 'therapist' || requiredRole === 'admin') {
        return null;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoginModal
            open={isLoginOpen}
            onOpenChange={handleModalClose}
            onSuccess={handleLoginSuccess}
          />
        </div>
      );
    }

    // Check if user has required role
    if (requiredRole && user.role !== requiredRole) {
      // Clear any saved redirect path since user doesn't have access
      sessionStorage.removeItem('redirect_after_login');

      return (
        <div className="container mx-auto px-4 py-8 max-w-md">
          <Card>
            <CardContent className="p-8 text-center">
              <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">
                You don't have permission to access this page.
              </p>
              <Button onClick={() => navigate('/')}>
                Go to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return <>{children}</>;
  } catch (error) {
    console.error("Error in ProtectedRoute:", error);
    // Render a fallback UI or redirect to an error page
    return (
      <div className="container mx-auto px-4 py-8 max-w-md">
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Authentication Error</h2>
            <p className="text-gray-600 mb-4">
              An unexpected error occurred. Please try logging in again.
            </p>
            <Button onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
};